/** @jsxImportSource @emotion/react */
import { useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { useParams, useNavigate } from 'react-router-dom'
import { Drawer, Box, Typography, useTheme } from '@mui/material'

import { Icon } from '@app/components/Atoms/Icon'
import { Button } from '@app/components/Atoms/Button'
import { toast } from '@app/components/ui/Toast/toast'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'

import { appLink } from '@app/config/appLink'
import { ERROR_403_MESSAGE } from '@app/app-constants'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import { useFetchAdaptationLibraryItem } from '../hooks/useFetchAdaptationLibrary'
import { COSTS_MAP, HAZARDS_COLOUR_MAP } from '../utils/adaptationLibrary.constants'
import {
  adaptationLibraryDrawerWrapper,
  adaptationLibraryCardBadge,
  adaptationLibraryCardBadges,
  adaptationLibraryTitle,
  adaptationLibraryDrawerItemWrapper,
  adaptationLibraryDrawerItem,
  adaptationLibraryDrawerDivider,
  adaptionLibraryButton,
  adaptationLibraryDrawerHeader,
} from '../styles/AdaptationLibrary.styles'

export const AdaptationLibraryDrawer = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const currencySymbol = useAtomValue(currencySymbolAtom)

  const { adaptationId } = useParams()
  const { data, isLoading, error } = useFetchAdaptationLibraryItem(adaptationId || '')

  useEffect(() => {
    if (error?.message === ERROR_403_MESSAGE) {
      toast({
        type: 'error',
        description:
          'You do not have permission to view this content. Please contact your administrator.',
        position: 'top-right',
      })
      navigate(appLink.homepage.path)
    }
  }, [error, navigate])

  if (isLoading) {
    return <LoadingSpinner />
  }
  if (!data) return null

  const {
    intervention_name,
    hazards_addressed,
    location,
    cost,
    description,
    timeframe,
    para_category,
    risk_management_type,
    lead_time,
    example,
    photo_url,
  } = data

  return (
    <Drawer anchor="right" open={true} onClose={() => navigate('/app/risk/adaptation-library')}>
      <Box css={adaptationLibraryDrawerWrapper()}>
        <Box css={adaptationLibraryDrawerHeader()}>
          <Typography css={{ fontSize: '20px', fontWeight: '500' }}>Intervention Detail</Typography>
          <Button variant="text" onClick={() => navigate('/app/risk/adaptation-library')}>
            <span>Close</span>
            <Icon colour="#0074CC" iconName="Close" />
          </Button>
        </Box>
        <Box css={{ padding: '16px 24px' }}>
          <Typography css={adaptationLibraryTitle()}>{intervention_name}</Typography>
          <Box css={adaptationLibraryCardBadges()}>
            {hazards_addressed.map((hazard) => {
              return (
                <Box
                  css={adaptationLibraryCardBadge({
                    theme,
                    color: HAZARDS_COLOUR_MAP[hazard].color || '#005C7A',
                    background: HAZARDS_COLOUR_MAP[hazard].background || '#BAFAFF',
                  })}
                >
                  {hazard}
                </Box>
              )
            })}
          </Box>
          {photo_url && (
            <img
              style={{ height: '300px', width: '100%', objectFit: 'cover', borderRadius: '6px' }}
              alt={intervention_name + ' image'}
              src={photo_url}
            />
          )}
          <Box css={adaptationLibraryDrawerItemWrapper()}>
            <Typography css={adaptationLibraryDrawerItem()}>
              Cost:
              <span style={{ fontWeight: 'normal' }}>
                {' ' +
                  COSTS_MAP[cost as keyof typeof COSTS_MAP].replaceAll('$', currencySymbol || '$')}
              </span>
            </Typography>
            <Typography css={adaptationLibraryDrawerItem()}>
              Timeline: <span style={{ fontWeight: 'normal' }}>{' ' + timeframe}</span>
            </Typography>
            <Typography css={adaptationLibraryDrawerItem()}>
              Para Category: <span style={{ fontWeight: 'normal' }}>{' ' + para_category}</span>
            </Typography>
            <Typography css={adaptationLibraryDrawerItem()}>
              Risk Management Type:
              <span style={{ fontWeight: 'normal' }}>{' ' + risk_management_type}</span>
            </Typography>
            <Typography css={adaptationLibraryDrawerItem()}>
              Location:
              <span style={{ fontWeight: 'normal' }}>{' ' + location.join(', ')}</span>
            </Typography>
            <Typography css={adaptationLibraryDrawerItem()}>
              Lead Time:
              <span style={{ fontWeight: 'normal' }}>{' ' + lead_time}</span>
            </Typography>
          </Box>
          <Box css={adaptationLibraryDrawerDivider()}>
            <b>Description</b>;
            <p>{description === 'null' ? 'No description available' : description}</p>
          </Box>
          {example && (
            <a
              href={example.replaceAll("'", '')}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', marginTop: '16px' }}
            >
              <Button css={adaptionLibraryButton()}>Example Link</Button>
            </a>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
